/**
 * IconButton 스타일
 * @description 앱 `IconButton.styles.ts` 의 웹 대응. 글리프는 두 크기 모두 20 이고 둘레
 *   패딩만 달라진다(sm 36 · md 44). 상자를 패딩에서 흘러나오게 두지 않고 `size` 로 못박는
 *   것도 앱과 같은 이유다 — 탭 타깃 크기가 부모의 사정에 좌우되면 안 된다. 값은 손으로
 *   적지 않고 토큰에서 계산한다(padding × 2 + icon).
 *
 *   `outlined` 는 `surface.disabled` 가 없다. 비활성에서 채움 없이 테두리와 흐린 글리프로만
 *   읽히는 것이 스펙이다.
 */
import { cva } from "class-variance-authority";

export const iconButtonStyles = cva(
  [
    "inline-flex shrink-0 items-center justify-center",
    "cursor-pointer select-none transition-colors",
    "bg-[var(--ibtn-surface,transparent)] text-[var(--ibtn-content)]",
    "shadow-[inset_0_0_0_var(--ibtn-stroke-width,0)_var(--ibtn-stroke,transparent)]",
    "hover:enabled:bg-[var(--ibtn-surface-hovered)]",
    "active:enabled:bg-[var(--ibtn-surface-pressed)]",
    "active:enabled:shadow-[inset_0_0_0_var(--ibtn-stroke-width-pressed,0)_var(--ibtn-stroke-pressed,transparent)]",
    "disabled:cursor-not-allowed",
    "disabled:bg-[var(--ibtn-surface-disabled,transparent)]",
    "disabled:text-[var(--ibtn-content-disabled)]",
    "disabled:shadow-[inset_0_0_0_var(--ibtn-stroke-width,0)_var(--ibtn-stroke-disabled,transparent)]",
    "focus-visible:outline-2 focus-visible:outline-offset-2",
    "focus-visible:outline-[var(--fw-color-border-focused)]",
    // 글리프는 색과 굵기를 버튼에서 물려받는다.
    "[&_svg]:size-[var(--ibtn-icon)] [&_svg]:stroke-[length:var(--ibtn-stroke-glyph)]",
  ],
  {
    variants: {
      tone: {
        neutral: [
          "[--ibtn-surface:var(--fw-icon-button-color-neutral-surface-rest)]",
          "[--ibtn-surface-hovered:var(--fw-icon-button-color-neutral-surface-hovered)]",
          "[--ibtn-surface-pressed:var(--fw-icon-button-color-neutral-surface-pressed)]",
          "[--ibtn-surface-disabled:var(--fw-icon-button-color-neutral-surface-disabled)]",
          "[--ibtn-content:var(--fw-icon-button-color-neutral-content-rest)]",
          "[--ibtn-content-disabled:var(--fw-icon-button-color-neutral-content-disabled)]",
        ],
        ghost: [
          "[--ibtn-surface-hovered:var(--fw-icon-button-color-ghost-surface-hovered)]",
          "[--ibtn-surface-pressed:var(--fw-icon-button-color-ghost-surface-pressed)]",
          "[--ibtn-surface-disabled:var(--fw-icon-button-color-ghost-surface-disabled)]",
          "[--ibtn-content:var(--fw-icon-button-color-ghost-content-rest)]",
          "[--ibtn-content-disabled:var(--fw-icon-button-color-ghost-content-disabled)]",
        ],
        outlined: [
          "[--ibtn-surface-hovered:var(--fw-icon-button-color-outlined-surface-hovered)]",
          "[--ibtn-surface-pressed:var(--fw-icon-button-color-outlined-surface-pressed)]",
          "[--ibtn-content:var(--fw-icon-button-color-outlined-content-rest)]",
          "[--ibtn-content-disabled:var(--fw-icon-button-color-outlined-content-disabled)]",
          "[--ibtn-stroke:var(--fw-icon-button-color-outlined-stroke-rest)]",
          "[--ibtn-stroke-pressed:var(--fw-icon-button-color-outlined-stroke-pressed)]",
          "[--ibtn-stroke-disabled:var(--fw-icon-button-color-outlined-stroke-disabled)]",
          "[--ibtn-stroke-width:var(--fw-icon-button-stroke-width-rest)]",
          "[--ibtn-stroke-width-pressed:var(--fw-icon-button-stroke-width-pressed)]",
        ],
        neutralInverse: [
          "[--ibtn-surface:var(--fw-icon-button-color-neutral-inverse-surface-rest)]",
          "[--ibtn-surface-hovered:var(--fw-icon-button-color-neutral-inverse-surface-hovered)]",
          "[--ibtn-surface-pressed:var(--fw-icon-button-color-neutral-inverse-surface-pressed)]",
          "[--ibtn-surface-disabled:var(--fw-icon-button-color-neutral-inverse-surface-disabled)]",
          "[--ibtn-content:var(--fw-icon-button-color-neutral-inverse-content-rest)]",
          "[--ibtn-content-disabled:var(--fw-icon-button-color-neutral-inverse-content-disabled)]",
        ],
        destructive: [
          "[--ibtn-surface:var(--fw-icon-button-color-destructive-surface-rest)]",
          "[--ibtn-surface-hovered:var(--fw-icon-button-color-destructive-surface-hovered)]",
          "[--ibtn-surface-pressed:var(--fw-icon-button-color-destructive-surface-pressed)]",
          "[--ibtn-surface-disabled:var(--fw-icon-button-color-destructive-surface-disabled)]",
          "[--ibtn-content:var(--fw-icon-button-color-destructive-content-rest)]",
          "[--ibtn-content-disabled:var(--fw-icon-button-color-destructive-content-disabled)]",
        ],
        brand: [
          "[--ibtn-surface:var(--fw-icon-button-color-brand-surface-rest)]",
          "[--ibtn-surface-hovered:var(--fw-icon-button-color-brand-surface-hovered)]",
          "[--ibtn-surface-pressed:var(--fw-icon-button-color-brand-surface-pressed)]",
          "[--ibtn-surface-disabled:var(--fw-icon-button-color-brand-surface-disabled)]",
          "[--ibtn-content:var(--fw-icon-button-color-brand-content-rest)]",
          "[--ibtn-content-disabled:var(--fw-icon-button-color-brand-content-disabled)]",
        ],
      },
      /** 상자 크기는 토큰에서 계산한다 — sm 8×2+20=36, md 12×2+20=44. */
      size: {
        sm: [
          "[--ibtn-icon:var(--fw-icon-button-sm-icon)]",
          "size-[calc(var(--fw-icon-button-sm-padding)*2+var(--fw-icon-button-sm-icon))]",
        ],
        md: [
          "[--ibtn-icon:var(--fw-icon-button-md-icon)]",
          "size-[calc(var(--fw-icon-button-md-padding)*2+var(--fw-icon-button-md-icon))]",
        ],
      },
      radius: {
        square: "rounded-[var(--fw-icon-button-radius-square)]",
        circle: "rounded-[var(--fw-icon-button-radius-circle)]",
      },
    },
    defaultVariants: { tone: "neutral", size: "md", radius: "square" },
  },
);
